/**
 * lib/divisions.ts
 * ------------------------------------------------------------------
 * School DIVISIONS — the academic section a class belongs to. A class
 * carries an explicit `division` field (SchoolClass.division); students
 * inherit it through `currentClass`. This is the Phase-1 spine.
 *
 * Deliberately self-contained (no imports) so `types/index.ts` can import
 * the `Division` type without a cycle, and helpers use structural params.
 *
 * NOT enforcement — Phase 2 adds the divisionAdmin role + AppUser.divisions[]
 * + a divisionAllows() scope helper. This module is just the vocabulary +
 * the level→division suggestion used by the seed and the ClassesSetup picker.
 * ------------------------------------------------------------------ */

export type Division =
  | 'preprimary'
  | 'juniorprimary'
  | 'upperprimary'
  | 'secondary';

export interface DivisionMeta {
  label: string;
  /** Level tokens that belong to this division — for the picker grouping and
   *  the seed's level→division mapping. */
  memberLevels: string[];
}

export const DIVISIONS: Record<Division, DivisionMeta> = {
  preprimary: { label: 'Pre-primary', memberLevels: ['Nursery', 'Reception'] },
  juniorprimary: {
    label: 'Junior Primary',
    memberLevels: ['Std 1', 'Std 2', 'Std 3'],
  },
  upperprimary: {
    label: 'Upper Primary',
    memberLevels: ['Std 4', 'Std 5', 'Std 6', 'Std 7'],
  },
  secondary: {
    label: 'Secondary',
    memberLevels: ['1', '2', '3', '4', '5', 'AS', 'A2'],
  },
};

/** Pre → Secondary, for ordered pickers. */
export const DIVISION_ORDER: Division[] = [
  'preprimary',
  'juniorprimary',
  'upperprimary',
  'secondary',
];

/** Human label ('' when unset). */
export function divisionLabel(d?: Division | null): string {
  return d ? DIVISIONS[d].label : '';
}

/**
 * The class's division — the explicit stored field. Structural param avoids
 * a types↔lib import cycle. This is the ONLY authoritative source at runtime;
 * never re-derive division from the class name.
 */
export function divisionOfClass(
  cls?: { division?: Division } | null,
): Division | undefined {
  return cls?.division ?? undefined;
}

/**
 * The subject's division — the explicit stored field when present, else DERIVED
 * from the deterministic seed id prefix (`subj_up_*` → upperprimary, `subj_jp_*`
 * → juniorprimary, everything else → secondary). This "resolve-onto-derivation"
 * shape (like resolveReportConfig) means subject pickers can scope by division
 * immediately, before any backfill sets the explicit field. Structural param
 * avoids a types↔lib cycle. Never re-derive from the subject NAME.
 */
export function divisionOfSubject(
  subject?: { id?: string; division?: Division } | null,
): Division | undefined {
  if (!subject) return undefined;
  if (subject.division) return subject.division;
  const id = subject.id ?? '';
  if (id.startsWith('subj_up_')) return 'upperprimary';
  if (id.startsWith('subj_jp_')) return 'juniorprimary';
  return 'secondary';
}

/** A teacher record's divisions[] (written by the B7b/c seed), String()-safe and
 *  narrowed to valid Division values. [] when absent — callers fall back to "all". */
/**
 * The ISLAMIC department is a teacher's division too (the school, 2026-10-09),
 * but not a class's: no class is Islamic, so it is kept OUT of `Division` and
 * of divisionsOfTeacher(), which every class-scoped rule reads (report access,
 * peer classes, a HOD's classes). It is ADDED to a record's `divisions[]`
 * beside whatever else it holds (Sheikh Hassan: secondary + islamic), never
 * in place of it. The Islamic screens' staff pickers list only records that
 * carry it.
 */
export const ISLAMIC_DIVISION = 'islamic';
export function hasIslamicDivision(teacher?: unknown): boolean {
  const raw = (teacher as { divisions?: unknown } | null | undefined)?.divisions;
  return Array.isArray(raw) && raw.map(String).includes(ISLAMIC_DIVISION);
}
/** A teacher's divisions for PHASE purposes: the class divisions, plus
 *  'islamic' when recorded (teacherPhases in lib/timetable reads it). */
export function phaseDivisionsOfTeacher(teacher?: unknown): string[] {
  return [...divisionsOfTeacher(teacher), ...(hasIslamicDivision(teacher) ? [ISLAMIC_DIVISION] : [])];
}

/** Every value in a teacher record's `divisions[]`, as stored (class
 *  divisions, 'islamic', and anything else), for the screen that edits it. */
export function rawDivisionsOf(teacher?: unknown): string[] {
  const raw = (teacher as { divisions?: unknown } | null | undefined)?.divisions;
  return Array.isArray(raw) ? raw.map(String) : [];
}

export function divisionsOfTeacher(teacher?: unknown): Division[] {
  const raw = (teacher as { divisions?: unknown } | null | undefined)?.divisions;
  if (!Array.isArray(raw)) return [];
  const valid = new Set<string>(DIVISION_ORDER);
  return raw.map((d) => String(d)).filter((d): d is Division => valid.has(d));
}

/**
 * COMMENT-ONLY class: mark entry saves the NARRATIVE ONLY (no attainment band)
 * and the Junior Primary report omits the A*–D legend + band column, widening
 * the comment. Standard 1 is comment-only school-wide (the first JP year is
 * assessed narratively, not banded); an explicit `commentOnly` flag on any
 * class forces it too, so this can later be toggled per class without a code
 * change. Structural param avoids a types↔lib import cycle; never re-derive
 * from anything but the stored `form`/flag.
 */
export function isCommentOnlyClass(
  cls?: { form?: string; commentOnly?: boolean } | null,
): boolean {
  if (!cls) return false;
  if (cls.commentOnly === true) return true;
  return /^(?:standard|std)\s*1\b/i.test(String(cls.form ?? '').trim());
}

/**
 * SUGGEST a division from a level/form token — for the seed + the ClassesSetup
 * picker's auto-fill only. The authoritative value is the stored
 * `class.division` field, not this function.
 *   Nursery/Reception → preprimary
 *   Std/Standard 1-3  → juniorprimary
 *   Std/Standard 4-7  → upperprimary
 *   Form 1-5 / AS / A2 / bare 1-5 (legacy `form`) → secondary
 */
export function divisionForLevel(
  level: string | null | undefined,
): Division | undefined {
  if (!level) return undefined;
  const v = level.trim().toLowerCase();
  if (v.startsWith('nursery') || v.startsWith('reception')) return 'preprimary';
  const std = v.match(/^(?:standard|std)\s*(\d)/);
  if (std) {
    const n = Number(std[1]);
    if (n >= 1 && n <= 3) return 'juniorprimary';
    if (n >= 4 && n <= 7) return 'upperprimary';
    return undefined;
  }
  if (v.startsWith('form') || v === 'as' || v === 'a2') return 'secondary';
  if (/^[1-5]$/.test(v)) return 'secondary';
  return undefined;
}
